import { useEffect, useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import compact from 'lodash/compact'
import includes from 'lodash/includes'
import range from 'lodash/range'
import { toJS } from 'mobx'
import moment from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Country from 'modules/core/components/Inputs/Country'
import Select from 'modules/core/components/Inputs/Select'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import ImportPublicHolidaysForm from 'modules/locations/lib/forms/ImportPublicHolidays'
import publicHolidays from 'modules/locations/store/collections/publicHolidays'
import companyHolidays, {
  locationHolidays,
} from 'modules/timeoff/store/collections/companyHolidays'

import Days from './Days'
import RegionsAndCitiesRow from './RegionsAndCitiesRow'

const YEARS_OFFSET = 4
interface Props {
  locationId: string
  onClose: () => void
}

const ImportHolidays = ({ locationId, onClose }: Props) => {
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    track(trackingEvents.location.importHolidaysStart, {
      event_category: 'Workplaces',
      location_id: locationId,
    })
  }, [])

  const importForm: ImportPublicHolidaysForm = useLazyRef(
    () =>
      new ImportPublicHolidaysForm({
        year: moment().year(),
      })
  ).current

  function fetchData() {
    setLoading(true)

    const year = importForm.get('year').value
    const from = dateToString(moment().year(year).startOf('year'), 'backend')
    const to = dateToString(moment().year(year).endOf('year'), 'backend')

    publicHolidays.fetch({
      data: {
        sort: 'date-asc',
        from,
        to,
        country: importForm.get('country').value,
        region: importForm.get('region').value,
      },
    })

    setLoading(false)
  }

  const years = locationHolidays(locationId).map((holiday) =>
    stringToDate(holiday.get('date'), 'backend').year()
  )

  const currentYear = moment().year()
  const minYear = Math.min(...years, currentYear - YEARS_OFFSET)

  const yearOptions = range(minYear, currentYear + YEARS_OFFSET).map((year) => {
    return {
      value: year,
      label: String(year),
    }
  })

  let ignoredHolidays: string[] = []

  const holidays = importForm.matchingHolidays(publicHolidays.toArray())

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const promises = compact(
      holidays.map(async (holiday) => {
        const id = String(holiday.attributes.id)

        if (includes(ignoredHolidays, id)) return null

        try {
          await companyHolidays.create({
            date: holiday.attributes.date,
            location_id: locationId,
            summary: holiday.attributes.summary,
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed creation', toJS(error))
        }
      })
    )

    const { success } = await asyncAction(() => Promise.all(promises))

    if (success) {
      const importedCount = holidays.length - ignoredHolidays.length
      track(trackingEvents.location.importHolidaysSubmit, {
        event_category: 'Workplaces',
        location_id: locationId,
        year: importForm.get('year').value,
        country: importForm.get('country').value,
        holidays_imported_count: importedCount,
      })
      closePortal()
    }
  }

  const onHandleChange = (newignoredHolidays: string[]) => {
    ignoredHolidays = newignoredHolidays
  }

  return (
    <Modal
      illustration="calendar"
      title={i18n.t('settings.time_off.import_company_holidays.title')}
      description={i18n.t('settings.time_off.import_company_holidays.subtitle')}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <Button
          disabled={loading || !holidays.length}
          submit
          extra={{ 'data-intercom-target': 'settings-location-import-holidays-form-submit' }}
        >
          {i18n.t('settings.time_off.import_company_holidays.title')}
        </Button>
      }
    >
      <FormRow multi extra={{ 'data-intercom-target': 'settings-location-import-holidays-form' }}>
        <Box className="settings_workplaces_import_bank_holidays_choose_country_drop_drown">
          <Country
            label={i18n.t('settings.time_off.country')}
            field={importForm.get('country')}
            none={i18n.t('common.choose')}
            onChange={fetchData}
          />
        </Box>
        <Select
          label={i18n.t('settings.time_off.year')}
          options={yearOptions}
          field={importForm.get('year')}
          onChange={fetchData}
        />
      </FormRow>
      {loading && <Loading />}
      {!loading && (
        <>
          <Box className="settings_workplaces_import_bank_holidays_choose_region_drop_drown">
            <RegionsAndCitiesRow
              form={importForm}
              publicHolidays={publicHolidays.toArray()}
              onChange={fetchData}
            />
          </Box>
          <Days
            onChange={onHandleChange}
            country={importForm.get('country').value}
            holidays={holidays}
          />
        </>
      )}
    </Modal>
  )
}

export default observer(ImportHolidays)
